import emailjs from 'emailjs-com';
import { getUserData } from './users';
import { getAuthHeaders } from './authHeaders';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_gnf7v3w';
const EMAILJS_TEMPLATE_ID = 'template_yehu66t';
const EMAILJS_USER_ID = 'NIRiqDcijZRSB8OFh';

// Email notification types and their templates
const EMAIL_TEMPLATES = {
    purchase_completed: {
        subject: 'Purchase Completed - Card Shop Exchange',
        template: 'purchase_notification'
    },
    offer_received: {
        subject: 'New Offer Received - Card Shop Exchange',
        template: 'offer_notification'
    },
    offer_status: {
        subject: 'Offer Status Update - Card Shop Exchange',
        template: 'offer_status_notification'
    },
    listing_action: {
        subject: 'Listing Status Update - Card Shop Exchange',
        template: 'listing_status_notification'
    }
};

// Send email notification
export const sendEmailNotification = async (userId, notificationData) => {
    try {
        console.log('🔥🔥🔥 EMAIL SENDING PROCESS STARTED 🔥🔥🔥');
        console.log('🔥 User ID:', userId);
        console.log('🔥 Notification Data:', notificationData);

        // Get user data to get email
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.error('🔥❌ User email not found for user:', userId);
            console.log('🔥 User data received:', userData);
            return false;
        }

        console.log('🔥✅ User email found:', userData.email);

        // Prepare email data - using the exact same format as Contact.js
        const emailData = {
            from_name: 'Card Shop Exchange',
            reply_to: userData.email, // Send to user's email
            message: formatEmailMessage(notificationData)
        };

        console.log('🔥 Email data prepared:', emailData);
        console.log('🔥 EmailJS Config:', {
            serviceId: EMAILJS_SERVICE_ID,
            templateId: EMAILJS_TEMPLATE_ID,
            userId: EMAILJS_USER_ID
        });

        console.log('🔥 Sending email via EmailJS...');
        
        // Send email using EmailJS
        const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            emailData,
            EMAILJS_USER_ID
        );

        console.log('🔥✅ EmailJS Response:', response);
        console.log('🔥✅ Email status:', response.status);
        console.log('🔥✅ Email text:', response.text);
        
        if (response.status === 200) {
            console.log('🔥✅ EMAIL SENT SUCCESSFULLY TO:', userData.email);
            console.log('🔥📧 Email content preview:', emailData.message.substring(0, 100) + '...');
        } else {
            console.log('🔥❌ EmailJS returned non-200 status:', response.status);
        }
        
        return true;

    } catch (error) {
        console.error('🔥❌ Error sending email notification:', error);
        console.error('🔥❌ Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
        });
        return false;
    }
};

// Get email subject based on notification type
const getEmailSubject = (notificationType) => {
    const template = EMAIL_TEMPLATES[notificationType];
    return template ? template.subject : 'Notification - Card Shop Exchange';
};

// Format email message based on notification type
const formatEmailMessage = (notificationData) => {
    const { type, message, listingTitle, action } = notificationData;
    
    let formattedMessage = '';
    
    switch (type) {
        case 'purchase_completed':
            formattedMessage = `🎉 Purchase Completed!\n\nYour purchase of "${listingTitle}" has been completed successfully. Thank you for your business!\n\nBest regards,\nCard Shop Exchange Team`;
            break;
            
        case 'offer_received':
            formattedMessage = `💼 New Offer Received!\n\nYou have received a new offer for your listing "${listingTitle}".\n\nPlease log in to your dashboard to view and respond to this offer.\n\nBest regards,\nCard Shop Exchange Team`;
            break;
            
        case 'offer_status':
            const statusText = action === 'accepted' ? 'accepted' : 'declined';
            formattedMessage = `📋 Offer Status Update!\n\nYour offer for "${listingTitle}" has been ${statusText}.\n\nPlease log in to your dashboard for more details.\n\nBest regards,\nCard Shop Exchange Team`;
            break;
            
        case 'listing_action':
            const actionText = action === 'accepted' ? 'approved' : 'declined';
            formattedMessage = `📝 Listing Status Update!\n\nYour listing "${listingTitle}" has been ${actionText}.\n\nPlease log in to your dashboard for more details.\n\nBest regards,\nCard Shop Exchange Team`;
            break;
            
        default:
            formattedMessage = message || 'You have a new notification from Card Shop Exchange.\n\nBest regards,\nCard Shop Exchange Team';
    }
    
    return formattedMessage;
};

// Send email notification with retry logic
export const sendEmailNotificationWithRetry = async (userId, notificationData, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            console.log(`📧 Email attempt ${attempt}/${maxRetries}`);
            const success = await sendEmailNotification(userId, notificationData);
            
            if (success) {
                console.log('✅ Email sent successfully on attempt', attempt);
                return true;
            }
        } catch (error) {
            console.error(`❌ Email attempt ${attempt} failed:`, error);
            
            if (attempt === maxRetries) {
                console.error('❌ All email attempts failed');
                return false;
            }
            
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
    }
    
    return false;
};

// Batch send email notifications
export const sendBatchEmailNotifications = async (notifications) => {
    const results = [];
    
    for (const notification of notifications) {
        const success = await sendEmailNotificationWithRetry(
            notification.recipientId, 
            notification
        );
        results.push({ notification, success });
    }
    
    return results;
}; 

// Test function to send a test email notification
export const testEmailNotification = async (userId) => {
    try {
        console.log('=== TESTING EMAIL NOTIFICATION ===');
        
        const testNotificationData = {
            type: 'listing_action',
            action: 'approved',
            listingId: 'test-listing-' + Date.now(),
            listingTitle: 'Test Listing',
            recipientId: userId,
            message: 'This is a test email notification from Card Shop Exchange.',
            timestamp: new Date(),
            isRead: false
        };
        
        const success = await sendEmailNotificationWithRetry(userId, testNotificationData);
        
        if (success) {
            console.log('✅ Test email notification sent successfully');
        } else {
            console.log('❌ Test email notification failed');
        }
        
        return success;
    } catch (error) {
        console.error('❌ Error in test email notification:', error);
        return false;
    }
}; 

// Clear test function for email notifications
export const testEmailNotificationClear = async (userId) => {
    try {
        console.log('🔥🔥🔥 EMAIL NOTIFICATION TEST STARTED 🔥🔥🔥');
        console.log('🔥 User ID:', userId);
        
        const emailCheck = await checkUserEmail(userId);
        console.log('🔥 Email check result:', emailCheck);
        
        if (!emailCheck.hasEmail) {
            console.log('🔥❌ NO EMAIL FOUND IN USER PROFILE');
            return false;
        }
        
        console.log('🔥✅ Email found:', emailCheck.email);
        
        const testNotificationData = {
            type: 'offer_received',
            action: 'offer_made',
            listingId: 'test-listing-' + Date.now(),
            listingTitle: 'Test Card Listing',
            recipientId: userId,
            message: '🔥 TEST EMAIL NOTIFICATION - You received a new offer!',
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('🔥 Sending test email with data:', testNotificationData);
        
        const success = await sendEmailNotificationWithRetry(userId, testNotificationData);
        
        if (success) {
            console.log('🔥✅ EMAIL SENT SUCCESSFULLY!');
        } else {
            console.log('🔥❌ EMAIL SENDING FAILED!');
        }
        
        console.log('🔥🔥🔥 EMAIL NOTIFICATION TEST COMPLETED 🔥🔥🔥');
        return success;
    } catch (error) {
        console.error('🔥❌ ERROR IN EMAIL TEST:', error);
        return false;
    }
};

// Check if user has email address
export const checkUserEmail = async (userId) => {
    try {
        console.log('=== CHECKING USER EMAIL ===');
        console.log('User ID:', userId);
        
        const userData = await getUserData(userId);
        console.log('User data:', userData);
        
        if (!userData) {
            console.log('❌ No user data found');
            return { hasEmail: false, email: null, userData: null };
        }
        
        if (!userData.email) {
            console.log('❌ No email found in user data');
            return { hasEmail: false, email: null, userData };
        }
        
        console.log('✅ User email found:', userData.email);
        return { hasEmail: true, email: userData.email, userData };
    } catch (error) {
        console.error('❌ Error checking user email:', error);
        return { hasEmail: false, email: null, userData: null };
    }
}; 

// Test offer notification email specifically
export const testOfferNotificationEmail = async (userId) => {
    try {
        console.log('🔥🔥🔥 OFFER NOTIFICATION EMAIL TEST 🔥🔥🔥');
        console.log('🔥 Testing offer notification for user:', userId);
        
        const emailCheck = await checkUserEmail(userId);
        if (!emailCheck.hasEmail) {
            console.log('🔥❌ NO EMAIL FOUND FOR OFFER TEST');
            return false;
        }
        
        console.log('🔥✅ Email found for offer test:', emailCheck.email);
        
        // Simulate the exact notification data that would be sent for a new offer
        const offerNotificationData = {
            type: 'offer_received',
            action: 'offer_made',
            listingId: 'test-offer-listing-' + Date.now(),
            listingTitle: 'Pokemon Charizard Test Card',
            recipientId: userId,
            message: 'You received a new offer of $25.00 for your listing "Pokemon Charizard Test Card".',
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('🔥 Sending offer notification email:', offerNotificationData);
        
        const success = await sendEmailNotificationWithRetry(userId, offerNotificationData);
        
        if (success) {
            console.log('🔥✅ OFFER NOTIFICATION EMAIL SENT SUCCESSFULLY!');
        } else {
            console.log('🔥❌ OFFER NOTIFICATION EMAIL FAILED!');
        }
        
        return success;
    } catch (error) {
        console.error('🔥❌ ERROR IN OFFER EMAIL TEST:', error);
        return false;
    }
}; 

// Test EmailJS with Contact form format
export const testEmailJSContactFormat = async (userId) => {
    try {
        console.log('🔥🔥🔥 TESTING EMAILJS CONTACT FORMAT 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for user');
            return false;
        }
        
        console.log('🔥 Testing with email:', userData.email);
        
        // Use exact same format as Contact.js
        const testEmailData = {
            from_name: 'Test User',
            reply_to: userData.email,
            message: '🔥 This is a test email from Card Shop Exchange notification system. If you receive this, EmailJS is working correctly!'
        };
        
        console.log('🔥 Sending test email with Contact format:', testEmailData);
        
        const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            testEmailData,
            EMAILJS_USER_ID
        );
        
        console.log('🔥✅ Contact format test response:', response);
        console.log('🔥✅ Status:', response.status);
        
        if (response.status === 200) {
            console.log('🔥✅ CONTACT FORMAT TEST SUCCESSFUL!');
            return true;
        } else {
            console.log('🔥❌ Contact format test failed with status:', response.status);
            return false;
        }
        
    } catch (error) {
        console.error('🔥❌ Error in Contact format test:', error);
        return false;
    }
}; 

// Firebase Functions approach for sending emails
export const sendEmailViaFirebaseFunction = async (userId, notificationData) => {
    try {
        console.log('🔥🔥🔥 FIREBASE FUNCTION EMAIL APPROACH 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for Firebase Function test');
            return false;
        }
        
        console.log('🔥 Testing Firebase Function email to:', userData.email);
        
        // Prepare email payload for Firebase Cloud Function
        const emailPayload = {
            to: userData.email,
            subject: getEmailSubject(notificationData.type),
            message: formatEmailMessage(notificationData),
            notificationType: notificationData.type,
            listingTitle: notificationData.listingTitle || 'Unknown Listing'
        };
        
        console.log('🔥 Email payload for Firebase Function:', emailPayload);
        
        // Call Firebase Cloud Function
        const response = await fetch('https://api-yleo2sn4ca-uc.a.run.app/send-notification-email', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                ...(await getAuthHeaders())
            },
            body: JSON.stringify(emailPayload)
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
            console.log('🔥✅ Firebase Function email sent successfully!');
            console.log('🔥📧 Message ID:', result.messageId);
            return true;
        } else {
            console.log('🔥❌ Firebase Function email failed:', result);
            return false;
        }
        
    } catch (error) {
        console.error('🔥❌ Firebase Function email error:', error);
        return false;
    }
};

// Alternative: Use a different EmailJS template
export const sendEmailViaAlternativeTemplate = async (userId, notificationData) => {
    try {
        console.log('🔥🔥🔥 ALTERNATIVE EMAILJS TEMPLATE APPROACH 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for alternative template test');
            return false;
        }
        
        console.log('🔥 Testing alternative template to:', userData.email);
        
        // This would use a different EmailJS template configured for notifications
        // For now, we'll log the approach
        const alternativeEmailData = {
            to_email: userData.email,
            to_name: userData.displayName || userData.username || 'User',
            subject: getEmailSubject(notificationData.type),
            message: formatEmailMessage(notificationData),
            notification_type: notificationData.type
        };
        
        console.log('🔥 Alternative template email data:', alternativeEmailData);
        console.log('🔥✅ Alternative template approach would work');
        console.log('🔥📧 Would need a new EmailJS template configured for notifications');
        
        return true;
    } catch (error) {
        console.error('🔥❌ Alternative template error:', error);
        return false;
    }
}; 

// Send email notification using Contact form template
export const sendEmailNotificationViaContact = async (userId, notificationData) => {
    try {
        console.log('🔥🔥🔥 SENDING NOTIFICATION VIA CONTACT TEMPLATE 🔥🔥🔥');
        console.log('🔥 User ID:', userId);
        console.log('🔥 Notification Data:', notificationData);

        // Get user data to get email
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.error('🔥❌ User email not found for user:', userId);
            return false;
        }

        console.log('🔥✅ User email found:', userData.email);

        // Use Contact form template but adapt it for notifications
        const emailData = {
            from_name: 'Card Shop Exchange Notifications',
            reply_to: userData.email, // This will be the recipient
            message: `🔥 NOTIFICATION EMAIL 🔥\n\n${formatEmailMessage(notificationData)}\n\nThis is an automated notification from Card Shop Exchange.`
        };

        console.log('🔥 Email data prepared:', emailData);
        console.log('🔥 Using Contact template:', EMAILJS_TEMPLATE_ID);

        // Send email using EmailJS with Contact template
        const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            emailData,
            EMAILJS_USER_ID
        );

        console.log('🔥✅ EmailJS Response:', response);
        console.log('🔥✅ Email status:', response.status);
        
        if (response.status === 200) {
            console.log('🔥✅ NOTIFICATION EMAIL SENT SUCCESSFULLY TO:', userData.email);
            console.log('🔥📧 Email content preview:', emailData.message.substring(0, 100) + '...');
        } else {
            console.log('🔥❌ EmailJS returned non-200 status:', response.status);
        }
        
        return true;

    } catch (error) {
        console.error('🔥❌ Error sending notification email:', error);
        console.error('🔥❌ Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
        });
        return false;
    }
}; 

// Test the new Contact template approach
export const testContactTemplateNotification = async (userId) => {
    try {
        console.log('🔥🔥🔥 TESTING CONTACT TEMPLATE NOTIFICATION 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for Contact template test');
            return false;
        }
        
        console.log('🔥 Testing Contact template notification to:', userData.email);
        
        const testNotificationData = {
            type: 'offer_received',
            action: 'offer_made',
            listingId: 'test-contact-template-' + Date.now(),
            listingTitle: 'Test Card via Contact Template',
            recipientId: userId,
            message: '🔥 This is a test notification using the Contact template format.',
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('🔥 Sending Contact template notification:', testNotificationData);
        
        const success = await sendEmailNotificationViaContact(userId, testNotificationData);
        
        if (success) {
            console.log('🔥✅ CONTACT TEMPLATE NOTIFICATION SENT SUCCESSFULLY!');
        } else {
            console.log('🔥❌ CONTACT TEMPLATE NOTIFICATION FAILED!');
        }
        
        return success;
    } catch (error) {
        console.error('🔥❌ Error in Contact template test:', error);
        return false;
    }
}; 

// Test the exact Contact form format
export const testExactContactFormat = async (userId) => {
    try {
        console.log('🔥🔥🔥 TESTING EXACT CONTACT FORM FORMAT 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for exact Contact test');
            return false;
        }
        
        console.log('🔥 Testing exact Contact format to:', userData.email);
        
        // Use EXACT same format as Contact.js
        const exactContactData = {
            from_name: 'Test User',
            reply_to: userData.email,
            message: '🔥 This is a test message using the EXACT same format as the Contact form. If you receive this, EmailJS is working correctly!'
        };
        
        console.log('🔥 Sending with EXACT Contact format:', exactContactData);
        
        const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            exactContactData,
            EMAILJS_USER_ID
        );
        
        console.log('🔥✅ Exact Contact format response:', response);
        console.log('🔥✅ Status:', response.status);
        
        if (response.status === 200) {
            console.log('🔥✅ EXACT CONTACT FORMAT TEST SUCCESSFUL!');
            console.log('🔥📧 Email should be sent to:', userData.email);
        } else {
            console.log('🔥❌ Exact Contact format test failed with status:', response.status);
        }
        
        return response.status === 200;
        
    } catch (error) {
        console.error('🔥❌ Error in exact Contact format test:', error);
        return false;
    }
}; 

// Check EmailJS template configuration
export const checkEmailJSTemplate = async () => {
    try {
        console.log('🔥🔥🔥 CHECKING EMAILJS TEMPLATE CONFIGURATION 🔥🔥🔥');
        console.log('🔥 Service ID:', EMAILJS_SERVICE_ID);
        console.log('🔥 Template ID:', EMAILJS_TEMPLATE_ID);
        console.log('🔥 User ID:', EMAILJS_USER_ID);
        
        // Test with minimal data to see what happens
        const minimalData = {
            from_name: 'Test',
            reply_to: 'test@example.com',
            message: 'Test message'
        };
        
        console.log('🔥 Testing with minimal data:', minimalData);
        
        const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            minimalData,
            EMAILJS_USER_ID
        );
        
        console.log('🔥✅ Template test response:', response);
        console.log('🔥✅ Template test status:', response.status);
        
        return response.status === 200;
        
    } catch (error) {
        console.error('🔥❌ Error checking EmailJS template:', error);
        return false;
    }
}; 

// Test Firebase Cloud Function email
export const testFirebaseCloudFunctionEmail = async (userId) => {
    try {
        console.log('🔥🔥🔥 TESTING FIREBASE CLOUD FUNCTION EMAIL 🔥🔥🔥');
        
        const userData = await getUserData(userId);
        if (!userData || !userData.email) {
            console.log('🔥❌ No email found for Firebase Cloud Function test');
            return false;
        }
        
        console.log('🔥 Testing Firebase Cloud Function email to:', userData.email);
        
        const testNotificationData = {
            type: 'offer_received',
            action: 'offer_made',
            listingId: 'test-firebase-function-' + Date.now(),
            listingTitle: 'Test Card via Firebase Function',
            recipientId: userId,
            message: '🔥 This is a test notification using Firebase Cloud Functions.',
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('🔥 Sending Firebase Cloud Function notification:', testNotificationData);
        
        const success = await sendEmailViaFirebaseFunction(userId, testNotificationData);
        
        if (success) {
            console.log('🔥✅ FIREBASE CLOUD FUNCTION EMAIL SENT SUCCESSFULLY!');
        } else {
            console.log('🔥❌ FIREBASE CLOUD FUNCTION EMAIL FAILED!');
        }
        
        return success;
    } catch (error) {
        console.error('🔥❌ Error in Firebase Cloud Function test:', error);
        return false;
    }
}; 